import { Icon } from '@iconify/react'
import { useState } from 'react'
import styled from 'styled-components'

const Overlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0,0,0,0.32);
   z-index: 140;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 32px 20px;
`

const Dialog = styled.div`
   width: 520px;
   max-width: calc(100% - 40px);
   background: #FFFFFF;
   border-radius: 16px;
   box-shadow: 0 14px 16px -2px rgba(16, 24, 40, 0.06), 0 4px 6px -2px rgba(16, 24, 40, 0.04);
   display: flex;
   flex-direction: column;
   overflow: hidden;
`

const Header = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 18px;

   h2 {
      margin: 0;
      font-size: 17px;
      font-weight: 700;
      color: #101018;
   }
`

const CloseButton = styled.button`
   background: none;
   border: none;
   color: #9C9C9F;
   cursor: pointer;
   padding: 4px;
   display: flex;
   align-items: center;
   transition: color 0.2s ease;

   &:hover {
      color: #3F3F45;
   }
`

const Body = styled.div`
   padding: 0 18px 18px 18px;
   color: #3F3F45;
   font-size: 14px;
   line-height: 24px;
`

const Description = styled.p`
   font-size: 14px;
   color: #9C9C9F;
   margin: 0 0 16px 0;
   line-height: 24px;
`

const InputGroup = styled.div`
   margin-bottom: 16px;
`

const Label = styled.label`
   display: block;
   font-size: 14px;
   font-weight: 500;
   color: #101018;
   margin-bottom: 8px;
`

const InputWrap = styled.div`
   position: relative;
`

const Input = styled.input`
   width: 100%;
   padding: 12px 40px 12px 12px;
   border: 1.6px solid #EDEDEE;
   border-radius: 8px;
   font-size: 14px;
   color: #101018;
   outline: none;
   transition: border 0.2s ease;
   background: #FFFFFF;
   box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.02);

   &:focus {
      border: 2px solid #C02236;
   }

   &::placeholder {
      color: #9C9C9F;
   }
`

const ToggleButton = styled.button`
   position: absolute;
   right: 12px;
   top: 50%;
   transform: translateY(-50%);
   background: none;
   border: none;
   color: #3F3F45;
   cursor: pointer;
   padding: 4px;
   display: flex;
   align-items: center;
`

const ErrorText = styled.span`
   display: block;
   font-size: 14px;
   color: #DD524D;
   margin-top: 8px;
`

const Divider = styled.div`
   background: #F5F5F6;
   height: 1px;
`

const Footer = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 12px;
   padding: 14px 18px 18px 18px;
   background: #FAFAFB;

   button {
      padding: 10px 24px;
      border-radius: 999px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      border: 0;
      transition: background .15s ease;
      display: inline-flex;
      gap: 6px;
      align-items: center;
      justify-content: center;
   }

   .confirm {
      background: #C02236;
      color: #FFFFFF;

      &:hover {
         background: #9a1a2a;
      }

      &:disabled {
         background: #F5F5F6;
         color: #9C9C9F;
         cursor: not-allowed;
      }
   }
`

interface Props {
   open: boolean
   onClose: () => void
   onSuccess: () => void
   passwordResetToken: string
}

export default function ResetPasswordModal({ open, onClose, onSuccess, passwordResetToken }: Props) {
   const [newPassword, setNewPassword] = useState('')
   const [confirmPassword, setConfirmPassword] = useState('')
   const [showNew, setShowNew] = useState(false)
   const [showConfirm, setShowConfirm] = useState(false)
   const [error, setError] = useState('')
   const [loading, setLoading] = useState(false)

   if (!open) return null

   const handleSubmit = async () => {
      setError('')

      if (!newPassword || !confirmPassword) {
         setError('Please fill in all fields')
         return
      }

      if (newPassword.length < 8) {
         setError('Password must be at least 8 characters')
         return
      }

      if (newPassword !== confirmPassword) {
         setError('Passwords do not match')
         return
      }

      setLoading(true)
      try {
         const { authService } = await import('../services/AuthService')
         const response = await authService.changePassword({
            passwordResetToken,
            newPassword,
            confirmPassword
         })

         if (response.status === 'success') {
            onSuccess()
         } else {
            setError(response.message || 'Failed to reset password')
         }
      } catch (err) {
         setError(err instanceof Error ? err.message : 'Failed to reset password')
      } finally {
         setLoading(false)
      }
   }

   return (
      <Overlay onClick={onClose}>
         <Dialog onClick={e => e.stopPropagation()}>
            <Header>
               <h2>Change Your Password</h2>
               <CloseButton onClick={onClose}>
                  <Icon icon='solar:close-circle-linear' width={24} />
               </CloseButton>
            </Header>
            <Body>
               <Description>
                  For security reasons, you need to change your password before continuing.
               </Description>

               <InputGroup>
                  <Label htmlFor="new-password">New Password</Label>
                  <InputWrap>
                     <Input
                        id="new-password"
                        type={showNew ? 'text' : 'password'}
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                     />
                     <ToggleButton type="button" onClick={() => setShowNew(!showNew)}>
                        <Icon icon={showNew ? 'solar:eye-closed-linear' : 'solar:eye-linear'} width={18} />
                     </ToggleButton>
                  </InputWrap>
               </InputGroup>

               <InputGroup>
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <InputWrap>
                     <Input
                        id="confirm-password"
                        type={showConfirm ? 'text' : 'password'}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                     />
                     <ToggleButton type="button" onClick={() => setShowConfirm(!showConfirm)}>
                        <Icon icon={showConfirm ? 'solar:eye-closed-linear' : 'solar:eye-linear'} width={18} />
                     </ToggleButton>
                  </InputWrap>
               </InputGroup>

               {error && <ErrorText>{error}</ErrorText>}
            </Body>
            <Divider />
            <Footer>
               <button className="confirm" onClick={handleSubmit} disabled={loading}>
                  {loading ? (
                     <>
                        <Icon icon="svg-spinners:ring-resize" width={16} />
                        Changing ...
                     </>
                  ) : 'Change Password'}
               </button>
            </Footer>
         </Dialog>
      </Overlay>
   )
}
